<template>
  <div v-if="$store.state.auth.status.isLoggedIn && !is_loading_resources_in_progress" class="flex h-screen min-h-screen">
    <!-- Static sidebar for desktop -->
    <div
      class="relative z-40 h-full max-h-screen duration-500 ease-in-out shadow-md select-none transition-width"
      :class="[!$store.state.ui.is_sidebar_collapsed ? 'w-64' : 'w-[73px]']">
      <div class="flex h-full lg:flex-shrink-0">
        <div
          class="relative flex flex-col pt-3 pb-0 pr-2 transition-all duration-500 ease-in-out bg-light-box-background dark:bg-dark-box-background group"
          :class="[!$store.state.ui.is_sidebar_collapsed ? 'w-64' : 'w-[73px]']">
          <div
            class="absolute z-0 flex w-5 transition ease-in-out transform rounded-lg top-9 -right-2 h-9 duration-side-menu-transition-duration bg-light-box-background dark:bg-dark-box-background shadow-c1">
            <ChevronLeft
              v-if="!$store.state.ui.is_sidebar_collapsed"
              class="text-xs cursor-pointer duration-side-menu-transition-duration text-light-brand-blue dark:text-light-brand-blue"
              @click="
                $store.dispatch('ui/setUIValue', {
                  is_sidebar_collapsed: !$store.state.ui.is_sidebar_collapsed
                })
              " />
            <ChevronRight
              v-if="$store.state.ui.is_sidebar_collapsed"
              class="text-xs cursor-pointer duration-side-menu-transition-duration text-light-brand-blue dark:text-light-brand-blue"
              @click="
                () => {
                  $store.dispatch('ui/setUIValue', {
                    is_sidebar_collapsed: !$store.state.ui.is_sidebar_collapsed
                  })
                }
              " />
          </div>
          <div class="flex items-center flex-shrink-0 overflow-hidden cursor-pointer">
            <div class="w-full px-6 py-3 pt-0 mt-4 mr-5 text-3xl font-semibold text-center text-blue">
              <LogoText v-if="!$store.state.ui.is_sidebar_collapsed" :dark-mode="$store.state.ui.darkMode" class="block z-3" />
              <Logo v-if="$store.state.ui.is_sidebar_collapsed" :dark-mode="$store.state.ui.darkMode" />
            </div>
          </div>

          <!-- Sidebar component(opened), swap this element with another sidebar if you like -->
          <div class="flex flex-col flex-1 h-0 overflow-x-hidden overflow-y-auto">
            <nav class="mt-3">
              <template v-for="item in filtered_navigation" :key="item.name">
                <div v-if="!item.children">
                  <!-- Sidebar items -->
                  <div
                    :class="[
                      currentTab === item.name
                        ? '!text-light-brand-blue dark:!text-dark-brand-blue cursor-default'
                        : 'cursor-pointer  text-light-grey-text dark:text-common-grey hover:text-gray-900',
                      'flex relative items-center h-16 px-6 py-4 text-base font-normal antialiased tracking-[0.001em] transform transition ease-in-out duration-side-menu-transition-duration '
                    ]"
                    @click="changeLeftSideTab(item)">
                    <!-- Sidebar icons -->
                    <component
                      :is="item.icon"
                      v-if="item.icon"
                      :active="currentTab === item.name"
                      :dark-mode="$store.state.ui.darkMode"
                      :class="'mr-3 flex-shrink-0 h-6 w-6'"
                      aria-hidden="true" />

                    <!-- Active left side div for highlighting -->
                    <div
                      :class="[
                        currentTab === item.name ? '' : '-translate-x-3',
                        'transition transform ease-in-out duration-side-menu-transition-duration w-[15px] h-full rounded-sm absolute bg-light-brand-blue dark:bg-dark-brand-blue -left-3'
                      ]" />
                    <div v-if="!$store.state.ui.is_sidebar_collapsed" :class="'ml-4'" @click="changeLeftSideTab(item)">
                      <div class="flex flex-col">
                        <span>{{ item.name }}</span>
                        <span v-if="!item.is_activated" class="text-sm">{{ $t("Get access") }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <Disclosure v-else v-slot="{ open }" as="div" class="space-y-1">
                  <DisclosureButton
                    :class="[
                      currentTab === item.name
                        ? 'bg-black text-gray-900 cursor-default'
                        : 'cursor-pointer  text-gray-500 hover:text-gray-900',
                      'flex w-full items-center pl-5 px-2 py-4 text-sm font-medium rounded-md'
                    ]"
                    @click="changeLeftSideTab(item)">
                    <component :is="item.icon" class="flex-shrink-0 w-6 h-6 mr-3" aria-hidden="true" />
                    <span class="flex-1 text-left">
                      {{ item.name }}
                    </span>
                    <svg
                      :class="[
                        open ? 'text-gray-400 rotate-90' : 'text-gray-300',
                        'ml-3 flex-shrink-0 h-5 w-5 transform hover:text-gray-400 transition-colors ease-in-out duration-150'
                      ]"
                      viewBox="0 0 20 20"
                      aria-hidden="true">
                      <path d="M6 6L14 10L6 14V6Z" fill="currentColor" />
                    </svg>
                  </DisclosureButton>
                  <DisclosurePanel class="space-y-1">
                    <div
                      v-for="subItem in item.children"
                      :key="subItem.name"
                      :class="[
                        currentTab === subItem.name
                          ? 'bg-gray-200 text-gray-900 cursor-default'
                          : 'cursor-pointer  text-gray-500 hover:text-gray-900',
                        'flex w-full pl-10 pr-2 py-2 items-center px-2 text-sm font-medium rounded-md'
                      ]"
                      @click="changeLeftSideTab(subItem)">
                      <component :is="subItem.icon" :class="'mr-3 flex-shrink-0 h-6 w-6'" aria-hidden="true" />
                      {{ subItem.name }}
                    </div>
                  </DisclosurePanel>
                </Disclosure>
              </template>
            </nav>
          </div>

          <!-- User profile button && display name & picture -->

          <Menu as="div" class="relative inline-block px-3 mt-6 text-left">
            <MenuButton class="w-full px-1 py-2 mb-2 text-sm font-medium text-left text-gray-700 rounded-md group">
              <span class="flex items-center justify-between w-full">
                <span class="flex items-center justify-between min-w-0 space-x-3">
                  <span v-if="$store.state.auth.user.avatar">
                    <img class="inline-block w-8 h-8 rounded-full" :src="$store.state.auth.user.avatar" alt="">
                  </span>
                  <span v-else>
                    <Avatar :first-name="$store.state.auth.user.first_name" :last-name="$store.state.auth.user.last_name" />
                  </span>
                  <span class="flex flex-col flex-1 min-w-0 pl-2">
                    <span class="text-black dark:text-white font-normal text-base subpixel-antialised tracking-[0.001em] truncate">{{
                      $store.state.auth.user.full_name
                    }}</span>
                    <span class="text-common-grey font-light text-xs subpixel-antialiased tracking-[0.001em] truncate">{{
                      $store.state.auth.user.email
                    }}</span>
                  </span>
                </span>
              </span>
            </MenuButton>

            <!-- Sidebar user menu -->
            <transition
              enter-active-class="transition duration-100 ease-out"
              enter-from-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100"
              leave-active-class="transition duration-75 ease-in"
              leave-from-class="transform scale-100 opacity-100"
              leave-to-class="transform scale-95 opacity-0">
              <!-- mx wil have to be adjusted on the "closed" variant to align with where the user picture is -->
              <MenuItems
                class="duration-200 fixed z-10 origin-top bottom-12 w-[230px] left-3 mb-6 rounded-md shadow-lg bg-white dark:bg-dark-screen-background ring-1 py-2 ring-blue ring-opacity-5 focus:outline-none">
                <div v-for="subItem in filtered_management_navigation" :key="subItem.name" class="cursor-pointer">
                  <div class="py-1">
                    <MenuItem>
                      <div class="flex">
                        <component
                          :is="subItem.icon"
                          v-if="subItem.icon"
                          class="flex-shrink-0 w-4 h-4 my-auto ml-4 mr-3"
                          aria-hidden="true" />
                        <Plus v-else :class="'mr-3 flex-shrink-0 h-4 w-4 my-auto ml-4 text-common-grey'" aria-hidden="true" />
                        <div class="text-gray-700 dark:text-dark-body-text" @click="changeLeftSideTab(subItem)">
                          {{
                            subItem.name === $t("Switch to Dark Mode") && $store.state.ui.darkMode
                              ? $t("Switch to Light Mode")
                              : subItem.name
                          }}
                        </div>
                      </div>
                    </MenuItem>
                  </div>
                </div>
              </MenuItems>
            </transition>
          </Menu>
        </div>
      </div>
    </div>
    <!-- Main column -->

    <div class="flex-1 w-full h-full overflow-auto overflow-x-hidden focus:outline-none">
      <router-view v-slot="{ Component }">
        <keep-alive>
          <component :is="Component" :key="$route.fullPath" />
        </keep-alive>
      </router-view>
    </div>
  </div>
  <div v-else>
    <LoadingProgressBar :percent="resources_loading_percentage" />
  </div>
</template>

<script>
import store from "@/store"
import { markRaw } from "vue"
import { Dialog, DialogOverlay, Menu, MenuButton, MenuItem, MenuItems, TransitionChild, TransitionRoot } from "@headlessui/vue"
import { LogOut, Palette, ChevronRight, Plus, ChevronLeft, Cog, List, House } from "lucide-vue-next"
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue"
import LogoText from "@/components/shared/LogoText.vue"
import Logo from "@/components/shared/Logo.vue"
import dataService from "@/services/data.service"
import Spinner from "@/components/shared/LoadingSpinner.vue"
import LoadingProgressBar from "@/components/shared/LoadingProgressBar.vue"
import Modals from "@/views/dashboard/Modals.vue"
import Avatar from "@/components/shared/Avatar.vue"
import GenericNewRecordModal from "@/views/dashboard/GenericNewRecordModal.vue"
import SearchPalette from "@/views/dashboard/SearchPalette.vue"
import { utils } from "@/store/helpers"
import { PAGE_REFERENCES } from "@/store/constants/references"

export default {
  name: "ShareSpaceMain",
  components: {
    SearchPalette,
    LoadingProgressBar,
    Avatar,
    Modals,
    Dialog,
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    Cog,
    DialogOverlay,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    TransitionChild,
    TransitionRoot,
    ChevronRight,
    ChevronLeft,
    Plus,
    LogoText,
    Logo,
    Spinner,
    Palette: markRaw(Palette),
    LogOut: markRaw(LogOut),
    GenericNewRecordModal,
    House: markRaw(House),
    List: markRaw(List)
  },
  provide: {
    /* View can be table or Form 
      page_refernece : portfolio,prospecting,forecasting... 
      object_type : company,contact ....
      view_type: table,form_create,form_update,rollups
    */

    getViewMetrics: ({ page_reference, object_type, view_type, flat = true }) => {
      let portfolio = store.state.portfolio
      let views_metrics = store.state.portfolio.views_metrics
      let view_reference = `${page_reference}>${view_type}>${object_type}`
      let metrics_refs = store.state.metrics.map((x) => x.reference_name)
      const returnMetricView = (new_list, update = true) => {
        setTimeout(() => {
          if (update) {
            views_metrics[view_reference] = new_list
            portfolio["views_metrics"] = views_metrics
            store.dispatch("setPortfolio", { views_metrics: views_metrics }, { root: true })
          }
        }, 100)
        if (flat) {
          return new_list.filter((x) => x && metrics_refs.includes(x))
        } else {
          return new_list
            .filter((x) => x && metrics_refs.includes(x))
            .map((ref) => store.state.metrics.find((metric) => metric.reference_name == ref))
            .filter((x) => x)
        }
      }

      const getViewDefaultMetrics = () => {
        let metrics_mapping = store.state.integration.metrics_mapping
        let default_metrics = []
        if (object_type == "company") {
          default_metrics = [
            metrics_mapping.company_name_metric,
            metrics_mapping.company_website_metric,
            metrics_mapping.company_industry_metric,
            metrics_mapping.company_base_renewal_value_metric,
            metrics_mapping.company_base_renewal_date_metric
          ]
        } else if (object_type == "contact") {
          default_metrics = [
            metrics_mapping.contact_first_name_metric,
            metrics_mapping.contact_last_name_metric,
            metrics_mapping.contact_email_metric,
            metrics_mapping.contact_relationship_strength_metric
          ]
        } else if (object_type == "opportunity") {
          default_metrics = [metrics_mapping.opportunity_name_metric, metrics_mapping.opportunity_amount_metric]
        } else if (object_type == "department") {
          default_metrics = [metrics_mapping.department_name_metric]
        } else {
          // return first 4
          default_metrics = utils
            .orderBy(
              store.state.metrics.filter((metric) => metric.object_type == object_type),
              ["order"]
            )
            .splice(0, 4)
            .map((m) => m.reference_name)
        }
        if (view_type == "rollups") {
          return default_metrics.filter((x) => x).filter((m) => m.aggregation_method)
        } else {
          return default_metrics.filter((x) => x)
        }
      }

      if (views_metrics && view_reference && Object.hasOwn(views_metrics, view_reference)) {
        // if the the same view exists return it
        return returnMetricView(views_metrics[view_reference], false)
      } else {
        // search for similar view and return it
        let similar_reference = null
        //find close sets of included metrics
        if (view_type == "form_create" || view_type == "form_update") {
          // return same level create metrics for update and vice versa
          if (
            view_type == "form_create" &&
            (similar_reference = Object.keys(views_metrics).find((k) => k.search(`${page_reference}>form_update>${object_type}`) != -1))
          ) {
            return returnMetricView(views_metrics[similar_reference])
          } else if (
            view_type == "form_update" &&
            (similar_reference = Object.keys(views_metrics).find((k) => k.search(`${page_reference}>form_create>${object_type}`) != -1))
          ) {
            return returnMetricView(views_metrics[similar_reference])
          }
          // ignore page and find any same view_type>object_type IE: in any page that the user might've opend the form before
          else if ((similar_reference = Object.keys(views_metrics).find((k) => k.search(`${view_type}>${object_type}`) != -1))) {
            return returnMetricView(views_metrics[similar_reference])
          } else if (
            view_type == "form_create" &&
            (similar_reference = Object.keys(views_metrics).find((k) => k.search(`form_create>${object_type}`) != -1))
          ) {
            return returnMetricView(views_metrics[similar_reference])
          } else if (
            view_type == "form_update" &&
            (similar_reference = Object.keys(views_metrics).find((k) => k.search(`form_update>${object_type}`) != -1))
          ) {
            return returnMetricView(views_metrics[similar_reference])
          } else if (
            view_type == "form_create" &&
            (similar_reference = Object.keys(views_metrics).find((k) => k.search(`form_update>${object_type}`) != -1))
          ) {
            // lastly create form metrics from any update form metrics
            return returnMetricView(views_metrics[similar_reference])
          } else if (
            view_type == "form_update" &&
            (similar_reference = Object.keys(views_metrics).find((k) => k.search(`form_create>${object_type}`) != -1))
          ) {
            // lastly update form metrics from any create form metrics
            return returnMetricView(views_metrics[similar_reference])
          } else {
            return returnMetricView(getViewDefaultMetrics())
          }
        } else if (
          view_type == "table" &&
          (similar_reference = Object.keys(views_metrics).find((k) => k.search(`table>${object_type}`) != -1))
        ) {
          // return any table metrics form same object_type ie: in any page the user viewed the same object in
          return returnMetricView(views_metrics[similar_reference])
        } else {
          // return default metrics
          return returnMetricView(getViewDefaultMetrics())
        }
      }
    },

    setViewMetrics: ({ page_reference, object_type, view_type, new_metrics_list }) => {
      let view_reference = `${page_reference}>${view_type}>${object_type}`
      let portfolio = store.state.portfolio
      let views_metrics = portfolio.views_metrics
      views_metrics[view_reference] = new_metrics_list
      portfolio["views_metrics"] = views_metrics
      store.dispatch("setPortfolio", { views_metrics: views_metrics }, { root: true })
    },
    removeMetricsFromView: (reference_names) => {
      let portfolio = store.state.portfolio
      let new_set = {}
      Object.keys(portfolio.views_metrics).forEach((key) => {
        Object.keys(portfolio.views_metrics)
        let value = portfolio.views_metrics[key]
        if (value) {
          new_set[key] = value.filter((x) => reference_names.indexOf(x) == -1)
        } else {
          new_set[key] = value
        }
      })

      portfolio["views_metrics"] = new_set
      store.dispatch("setPortfolio", { views_metrics: new_set }, { root: true })
    },

    getPageTabs: ({ page_reference: page_reference, flat = false }) => {
      let portfolio = store.state.portfolio
      let pages_tabs = portfolio.pages_tabs
      if (Object.hasOwn(pages_tabs, page_reference)) {
        if (flat) {
          return pages_tabs[page_reference].filter((x) => x)
        } else {
          return pages_tabs[page_reference].map((t) => store.state.object_types.find((obj) => obj.type == t)).filter((x) => x)
        }
      } else {
        let new_tabs_list = store.state.object_types.map((obj) => obj.type)
        if (page_reference == PAGE_REFERENCES.COMPANY_PROFILE) {
          new_tabs_list = new_tabs_list.filter((ob) => ob != "company")
        }
        pages_tabs[page_reference] = new_tabs_list
        portfolio["pages_tabs"] = pages_tabs
        store.dispatch("setPortfolio", { pages_tabs: pages_tabs }, { root: true })

        if (flat) {
          return new_tabs_list.filter((x) => x)
        } else {
          return new_tabs_list.map((t) => store.state.object_types.find((obj) => obj.type == t)).filter((x) => x)
        }
      }
    },
    setPageTabs: ({ page_reference, new_tabs_list }) => {
      let portfolio = store.state.portfolio
      let pages_tabs = portfolio.pages_tabs
      pages_tabs[page_reference] = new_tabs_list
      portfolio["pages_tabs"] = pages_tabs
      store.dispatch("setPortfolio", { pages_tabs: pages_tabs }, { root: true })
    },

    getPageGraphs: ({ page_reference: page_reference, object_type: object_type, flat = false }) => {
      let view_reference = `${page_reference}`
      if (object_type) {
        view_reference = `${page_reference}>${object_type}`
      }
      let portfolio = store.state.portfolio
      let pages_graphs = portfolio.pages_graphs
      if (Object.hasOwn(pages_graphs, view_reference)) {
        if (flat) {
          return pages_graphs[view_reference].filter((x) => x)
        } else {
          return pages_graphs[view_reference].map((t) => store.state.data.all_graphs.find((graph) => graph.order == t)).filter((x) => x)
        }
      } else {
        let new_graphs_list = []
        pages_graphs[view_reference] = new_graphs_list
        portfolio["pages_graphs"] = pages_graphs
        store.dispatch("setPortfolio", { pages_graphs: pages_graphs }, { root: true })

        if (flat) {
          return new_graphs_list.filter((x) => x)
        } else {
          return new_graphs_list.map((t) => store.state.data.all_graphs.find((graph) => graph.order == t)).filter((x) => x)
        }
      }
    },
    setPageGraphs: ({ page_reference, object_type, new_graphs_list }) => {
      let view_reference = `${page_reference}`
      if (object_type) {
        view_reference = `${page_reference}>${object_type}`
      }
      let portfolio = store.state.portfolio
      let pages_graphs = portfolio.pages_graphs
      pages_graphs[view_reference] = new_graphs_list
      portfolio["pages_graphs"] = pages_graphs
      store.dispatch("setPortfolio", { pages_graphs: pages_graphs }, { root: true })
    },
    addPageGraph: ({ page_reference, object_type, graph }) => {
      let view_reference = `${page_reference}`
      if (object_type) {
        view_reference = `${page_reference}>${object_type}`
      }
      let portfolio = store.state.portfolio
      let pages_graphs = portfolio.pages_graphs
      if (Object.hasOwn(pages_graphs, view_reference)) {
        pages_graphs[view_reference] = [...pages_graphs[view_reference], graph.order]
      } else {
        pages_graphs[view_reference] = [graph.order]
      }
      portfolio["pages_graphs"] = pages_graphs
      store.dispatch("setPortfolio", { pages_graphs: pages_graphs }, { root: true })
    }
  },
  data() {
    return {
      current_plan: null,
      navigation: [
        // {
        //   name: "Home",
        //   href: `/workspace/${this.$store.state.auth.user?.organization?.id}`,
        //   icon: House,
        //   is_activated: true,
        //   current: true
        // },
      ],

      management_navigation: [
        {
          name: this.$t("Global Settings"),
          href: null,
          icon: Cog,
          current: false,

          children: [
            {
              name: this.$t("Switch to Dark Mode"),
              action: "switch-to-dark-mode",
              href: "",
              icon: Palette,
              current: false
            },
            {
              name: this.$t("Sign Out"),
              href: "",
              action: "sign-out",
              icon: LogOut,
              current: false
            }
          ]
        }
      ],

      windowWidth: window.innerHeight,

      is_loading_resources_in_progress: true,
      resources_loading_percentage: 0
    }
  },
  computed: {
    currentTab() {
      return this.$store.state.ui.currentSelectedLeftMenuTab
    },
    filtered_navigation() {
      return this.navigation
    },
    filtered_management_navigation() {
      const tabs = [
        ...this.management_navigation[0].children
          .filter((n) => this.$store.state.control.actions_menu.findIndex((x) => x.module.name.toLowerCase() == n.name.toLowerCase()) != -1)
          .map((n) => {
            let r = {
              ...n,
              ...this.$store.state.control.actions_menu.find((x) => x.module.name.toLowerCase() == n.name.toLowerCase())
            }
            if ("children" in r) {
              delete r["children"]
            }
            return r
          })
      ]
      return tabs
    }
  },
  watch: {
    windowWidth(newWidth) {
      if (newWidth < 768 != this.$store.state.ui.is_sidebar_collapsed) {
        this.$store.dispatch("ui/setUIValue", {
          is_sidebar_collapsed: newWidth < 768
        })
      }
    },
    $route() {
      this.setSelectedTab()
    }
  },
  created() {
    this.getAvailableSharedObjects()
    this.setSelectedTab()
    //can be removed if main page will have different orgainzations maybe later
    if (this.$store.state.auth.user.organization) {
      this.$router.replace(`/share-space/${this.$store.state.organization.id}`)
    }

    dataService.getPlan(this.$route.params.object_id).then((res) => {
      this.current_plan = res.data
    })
    this.init_realtime_listener()

    let promises = [
      this.$store.dispatch("control/getMenus"),
      this.$store.dispatch("getPortfolio"),
      this.$store.dispatch("integration/getMetricsMapping")
    ]
    Promise.all(
      promises.map((promise) => {
        return promise.then(() => {
          this.resources_loading_percentage = this.resources_loading_percentage + 100 / 2
        })
      })
    ).then(() => {
      this.is_loading_resources_in_progress = false
    })

    window.addEventListener(
      "keydown",
      (e) => {
        if ((e.ctrlKey && e.keyCode === 75) || (e.metaKey && e.keyCode === 75)) {
          this.$store.commit("ui/toggleSearchPalette", true)
          e.preventDefault()
        }
      },
      true
    )
  },

  mounted() {
    this.$nextTick(() => {
      window.addEventListener("resize", this.onResize)
    })
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.onResize)
  },

  activated() {
    this.register_listeners()
  },

  methods: {
    getAvailableSharedObjects() {
      let params = {
        page_size: 1
      }
      params["prevent_loading"] = true

      dataService.getPlans(params).then((res) => {
        if (res.data.results.length) {
          this.navigation.push({
            name: "Plans",
            href: `/share-space/${this.$store.state.auth.user?.organization?.id}/plans`,

            icon: List,
            is_activated: true,
            current: true
          })
        }

        this.navigation.push({
          name: "Dashboard Pages",
          href: `/share-space/${this.$store.state.auth.user?.organization?.id}/dashboard`,

          icon: List,
          is_activated: true,
          current: true
        })
      })

      dataService.getGoals(params).then((res) => {
        if (res.data.results.length) {
          this.navigation.push({
            name: "Glans",
            href: `/share-space/${this.$store.state.auth.user?.organization?.id}/goals`,

            icon: List,
            is_activated: true,
            current: true
          })
        }
      })

      dataService.getTasks(params).then((res) => {
        if (res.data.results.length) {
          this.navigation.push({
            name: "Tasks",
            href: `/share-space/${this.$store.state.auth.user?.organization?.id}/tasks`,

            icon: List,
            is_activated: true,
            current: true
          })
        }
      })
    },
    setSelectedTab() {
      const route_params = this.$route.params
      if (route_params.org) {
        if (route_params.object_type) {
          if (this.navigation.findIndex((nav_item) => nav_item.name.toLowerCase() == route_params.object_type) !== -1) {
            this.$store.dispatch("ui/setCurrentSelectedTab", this.$utils.capitalize(route_params.object_type))
          }
          //wrong object type
          else {
            this.$router.replace(`/share-space/${route_params.org}/`)
          }
        } else {
          this.$store.dispatch("ui/setCurrentSelectedTab", "Home")
        }
      }
    },
    init_realtime_listener() {
      this.$store.dispatch("realtime/connect")
    },

    handleThemeChange() {
      const darkMode = this.$store.state.ui.darkMode
      window.localStorage.setItem("dark-mode", darkMode ? false : true)
      this.$store.dispatch("ui/toggleTheme")
    },
    register_listeners() {},
    onResize() {
      this.windowWidth = window.innerWidth
    },
    changeLeftSideTab(item) {
      if (item.action == "switch-to-dark-mode") {
        this.handleThemeChange()
        return
      }
      if (item.action == "sign-out") {
        this.$store.dispatch("forceLogout")
        return
      }
      this.$store.dispatch("ui/setCurrentSelectedTab", item.name)
      if (item.href != null) {
        this.$router.push(item.href)
      }
    }
  }
}
</script>
